import React, { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Bell,
  BellOff,
  BellRing,
  Send,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Smartphone,
  Settings
} from 'lucide-react';
import { toast } from 'sonner';

const PushNotifications = () => {
  const [loading, setLoading] = useState(true);
  const [subscribed, setSubscribed] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [history, setHistory] = useState([]);
  const [notificationSupported, setNotificationSupported] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState('default');

  useEffect(() => {
    checkNotificationSupport();
    loadStatus();
    loadHistory();
  }, []);

  const checkNotificationSupport = () => {
    const supported = 'Notification' in window && 'serviceWorker' in navigator;
    setNotificationSupported(supported);
    if (supported) {
      setPermissionStatus(Notification.permission);
    }
  };

  const loadStatus = async () => {
    try {
      const response = await adminAPI.getNotificationStatus();
      setSubscribed(response.data.subscribed);
    } catch (error) {
      console.error('Error loading notification status:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const response = await adminAPI.getNotificationHistory({ limit: 10 });
      setHistory(response.data.notifications || []);
    } catch (error) {
      console.error('Error loading notification history:', error);
    }
  };

  const requestPermission = async () => {
    if (!notificationSupported) {
      toast.error('Les notifications ne sont pas supportées par ce navigateur');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      setPermissionStatus(permission);
      
      if (permission === 'granted') {
        toast.success('Permissions accordées !');
        return true;
      } else if (permission === 'denied') {
        toast.error('Permissions refusées. Activez-les dans les paramètres du navigateur.');
        return false;
      }
      return false;
    } catch (error) {
      console.error('Error requesting permission:', error);
      toast.error('Erreur lors de la demande de permission');
      return false;
    }
  };

  const handleSubscribe = async () => {
    setSubscribing(true);
    
    try {
      // First, request permission if not granted
      if (permissionStatus !== 'granted') {
        const granted = await requestPermission();
        if (!granted) {
          setSubscribing(false);
          return;
        }
      }

      // For a full implementation, you would:
      // 1. Register a service worker
      // 2. Get a push subscription from the browser
      // 3. Send it to the backend
      
      // Simplified version - just register interest
      const mockSubscription = {
        endpoint: `https://notifications.dockhta.com/${Date.now()}`,
        keys: {
          p256dh: 'mock-key',
          auth: 'mock-auth'
        }
      };

      await adminAPI.subscribePush(mockSubscription);
      setSubscribed(true);
      toast.success('Notifications activées !');
      
      // Show a test notification locally
      if (Notification.permission === 'granted') {
        new Notification('DOCKHTA Admin', {
          body: 'Notifications activées avec succès !',
          icon: '/logo192.png'
        });
      }
      
    } catch (error) {
      console.error('Error subscribing:', error);
      toast.error('Erreur lors de l\'activation');
    } finally {
      setSubscribing(false);
    }
  };

  const handleUnsubscribe = async () => {
    setSubscribing(true);
    
    try {
      await adminAPI.unsubscribePush();
      setSubscribed(false);
      toast.success('Notifications désactivées');
    } catch (error) {
      console.error('Error unsubscribing:', error);
      toast.error('Erreur lors de la désactivation');
    } finally {
      setSubscribing(false);
    }
  };

  const handleSendTest = async () => {
    setSendingTest(true);
    
    try {
      await adminAPI.sendTestNotification();
      toast.success('Notification de test envoyée !');
      
      // Show local notification
      if (Notification.permission === 'granted') {
        new Notification('Test de notification', {
          body: 'Les notifications DOCKHTA fonctionnent correctement !',
          icon: '/logo192.png',
          tag: 'test-notification'
        });
      }
      
      loadHistory();
    } catch (error) {
      console.error('Error sending test:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingTest(false);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'test': return <Bell className="w-4 h-4 text-blue-500" />;
      case 'kyc_alert': return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      case 'urgent': return <BellRing className="w-4 h-4 text-red-500" />;
      default: return <Bell className="w-4 h-4 text-slate-500" />;
    }
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-slate-300 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="push-notifications">
      {/* Status Card */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              subscribed ? 'bg-emerald-100' : 'bg-slate-100'
            }`}>
              {subscribed ? (
                <Bell className="w-5 h-5 text-emerald-600" />
              ) : (
                <BellOff className="w-5 h-5 text-slate-400" />
              )}
            </div>
            <div>
              <CardTitle className="text-lg">Notifications Push</CardTitle>
              <CardDescription>
                Recevez des alertes pour les actions urgentes
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Browser Support Warning */}
          {!notificationSupported && (
            <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Navigateur non supporté</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Les notifications push ne sont pas disponibles sur ce navigateur.
                    Utilisez Chrome, Firefox ou Edge pour activer cette fonctionnalité.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Permission Status */}
          {notificationSupported && permissionStatus === 'denied' && (
            <div className="p-4 bg-red-50 rounded-xl border border-red-200">
              <div className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-red-500 mt-0.5" />
                <div>
                  <p className="font-medium text-red-800">Permissions bloquées</p>
                  <p className="text-sm text-red-700 mt-1">
                    Les notifications ont été bloquées. Pour les activer, cliquez sur l'icône
                    de cadenas dans la barre d'adresse et autorisez les notifications.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Subscription Toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
            <div className="flex items-center gap-3">
              <Smartphone className="w-5 h-5 text-slate-500" />
              <div>
                <p className="font-medium text-slate-900">Notifications sur ce navigateur</p>
                <p className="text-sm text-slate-500">
                  {subscribed ? 'Activées' : 'Désactivées'}
                </p>
              </div>
            </div>
            
            <Switch
              checked={subscribed}
              onCheckedChange={subscribed ? handleUnsubscribe : handleSubscribe}
              disabled={subscribing || !notificationSupported || permissionStatus === 'denied'}
              data-testid="notification-toggle"
            />
          </div>

          {/* Test Button */}
          {subscribed && (
            <Button
              variant="outline"
              onClick={handleSendTest}
              disabled={sendingTest}
              className="w-full"
              data-testid="send-test-notification"
            >
              {sendingTest ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Envoyer une notification test
            </Button>
          )}

          {/* Notification Types Info */}
          <div className="space-y-3">
            <h4 className="font-medium text-slate-900 text-sm">Types de notifications</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-start gap-2 p-3 bg-white rounded-lg border">
                <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5" />
                <div>
                  <p className="text-sm font-medium">KYC en attente</p>
                  <p className="text-xs text-slate-500">Dossiers &gt; 7 jours</p>
                </div>
              </div>
              <div className="flex items-start gap-2 p-3 bg-white rounded-lg border">
                <Clock className="w-4 h-4 text-blue-500 mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Validation pro</p>
                  <p className="text-xs text-slate-500">Profils en attente</p>
                </div>
              </div>
              <div className="flex items-start gap-2 p-3 bg-white rounded-lg border">
                <XCircle className="w-4 h-4 text-red-500 mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Annulations élevées</p>
                  <p className="text-xs text-slate-500">Taux &gt; 30%</p>
                </div>
              </div>
              <div className="flex items-start gap-2 p-3 bg-white rounded-lg border">
                <BellRing className="w-4 h-4 text-purple-500 mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Erreurs système</p>
                  <p className="text-xs text-slate-500">Problèmes critiques</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notification History */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Historique des notifications</CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Aucune notification envoyée</p>
            </div>
          ) : (
            <ScrollArea className="h-[200px]">
              <div className="space-y-2">
                {history.map((notif, index) => (
                  <div
                    key={notif.id || index}
                    className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg"
                  >
                    {getNotificationIcon(notif.type)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {notif.title}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {notif.body}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {new Date(notif.created_at).toLocaleString('fr-FR')}
                      </p>
                    </div>
                    <Badge className={
                      notif.status === 'sent' 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : 'bg-slate-100 text-slate-700'
                    }>
                      {notif.status === 'sent' ? 'Envoyée' : notif.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PushNotifications;
